import {
  ABA_LABELS,
  type AbaPrincipal,
  isAbaPrincipal,
  moduloFixoDaUrl,
} from '../pages/corte/abasModulo';
import { garantirSessaoDesktop } from './adeSessaoDesktop';

type NwWindow = {
  focus?: () => void;
  window?: Window & { location?: Location };
};

type NwRuntime = {
  Window?: {
    get?: () => NwWindow;
    getAll?: () => NwWindow[];
    open?: (
      url: string,
      options: Record<string, unknown>,
      callback?: (win: NwWindow) => void
    ) => void;
  };
};

function nw(): NwRuntime | undefined {
  return (window as unknown as { nw?: NwRuntime }).nw;
}

export function nwDisponivel(): boolean {
  return Boolean(nw()?.Window?.open);
}

export function isJanelaModuloFilha(): boolean {
  return moduloFixoDaUrl() !== null;
}

/** Foca a janela principal (sem parâmetro `modulo` na URL). */
export function focarJanelaPrincipal(): void {
  const runtime = nw();
  if (!runtime?.Window?.getAll) return;
  try {
    for (const janela of runtime.Window.getAll()) {
      const href = janela.window?.location?.href ?? '';
      if (!href.includes('modulo=')) {
        janela.focus?.();
        return;
      }
    }
  } catch {
    /* NW.js indisponível */
  }
}

/** Foca a janela Projeto: filha `modulo=projeto` se existir; senão a principal. */
export function focarJanelaProjeto(): void {
  const runtime = nw();
  if (!runtime?.Window?.getAll) return;
  try {
    for (const janela of runtime.Window.getAll()) {
      const href = janela.window?.location?.href ?? '';
      if (href.includes('modulo=projeto')) {
        janela.focus?.();
        return;
      }
    }
    focarJanelaPrincipal();
  } catch {
    /* NW.js indisponível */
  }
}

/** Foca a janela Relatório: filha `modulo=relatorio` se existir; senão a principal. */
export function focarJanelaRelatorio(): void {
  const runtime = nw();
  if (!runtime?.Window?.getAll) return;
  try {
    for (const janela of runtime.Window.getAll()) {
      const href = janela.window?.location?.href ?? '';
      if (href.includes('modulo=relatorio')) {
        janela.focus?.();
        return;
      }
    }
    focarJanelaPrincipal();
  } catch {
    /* NW.js indisponível */
  }
}

/** Há janela dedicada ao Relatório aberta? */
export function janelaRelatorioAberta(): boolean {
  const runtime = nw();
  if (!runtime?.Window?.getAll) return false;
  try {
    for (const janela of runtime.Window.getAll()) {
      const href = janela.window?.location?.href ?? '';
      if (href.includes('modulo=relatorio')) return true;
    }
  } catch {
    /* ignora */
  }
  return false;
}

function urlRelativaModulo(aba: AbaPrincipal, token: string | null): string {
  const qs = new URLSearchParams();
  qs.set('modulo', aba);
  qs.set('ade_auth', '1');
  if (token) qs.set('ade_sessao', token);
  return `index.html?${qs.toString()}`;
}

/** Abre ou foca janela nativa do módulo. */
export function abrirModuloEmJanela(aba: AbaPrincipal): void {
  const runtime = nw();
  if (!runtime?.Window?.open) return;

  const token = garantirSessaoDesktop();
  const relativeUrl = urlRelativaModulo(aba, token);

  runtime.Window.open(
    relativeUrl,
    {
      id: `ade-modulo-${aba}`,
      focus: true,
      show: true,
      width: 1280,
      height: 860,
      min_width: 720,
      min_height: 520,
      position: 'center',
      resizable: true,
      frame: true,
      title: `Sistema Ade — ${ABA_LABELS[aba]}`,
    },
    (janela) => {
      try {
        const cw = janela.window;
        if (cw?.location) {
          const href = cw.location.href || '';
          if (!href.includes(`modulo=${aba}`)) {
            cw.location.href = relativeUrl;
          }
        }
        janela.focus?.();
      } catch {
        /* ignora */
      }
    }
  );
}

export function navegarParaModulo(
  aba: AbaPrincipal,
  ctx: {
    isJanelaFilha: boolean;
    moduloFixo: AbaPrincipal | null;
    abaAtiva: AbaPrincipal;
    setAbaAtiva: (aba: AbaPrincipal) => void;
  }
): void {
  if (!isAbaPrincipal(aba)) return;

  if (!nwDisponivel()) {
    ctx.setAbaAtiva(aba);
    return;
  }

  if (aba === 'projeto') {
    if (ctx.isJanelaFilha) {
      focarJanelaProjeto();
    } else {
      ctx.setAbaAtiva('projeto');
    }
    return;
  }

  if (ctx.isJanelaFilha && ctx.moduloFixo === aba) return;
  if (!ctx.isJanelaFilha && ctx.abaAtiva === aba) return;

  abrirModuloEmJanela(aba);
}

export function tituloJanelaModulo(aba: AbaPrincipal | null): string {
  if (aba) return `Sistema Ade — ${ABA_LABELS[aba]}`;
  return 'Sistema Ade — Projeto';
}
